import { useQuery } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router";

import type { AnswerItem } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { DocumentPaper } from "@/components/Paper/DocumentPaper";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { surveyMutations, useSurveyQueries } from "@/queries/surveys";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { SurveyInfoSection } from "../Details/components/SurveyInfoSection";
import { SurveyRespondQuestionCard } from "./components/SurveyRespondQuestionCard";
import { SurveyRespondQuestionGroupCard } from "./components/SurveyRespondQuestionGroupCard";
import { SurveyRespondSuccessModal } from "./components/SurveyRespondSuccessModal";

export type SurveyRespondFormValues = {
  [questionId: string]: Omit<AnswerItem, "questionId">;
};

export function SurveyRespondPage(): React.ReactNode {
  const [isSuccessModalOpened, successModalHandler] = useBool(false);

  const { id: surveyId } = useParams<{ id: string }>();
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const slug = useSlug();
  const postHog = usePostHog();
  const surveyQueries = useSurveyQueries();
  const {
    data: surveyDetails,
    error: errorSurveyDetails,
    isPending: isPendingSurveyDetails,
  } = useQuery({
    ...surveyQueries.details({ surveyId: surveyId!, category: locationState?.category || "feelAtHome" }),
    enabled: !!surveyId,
  });
  const addSurveyAnswersMutation = surveyMutations.useAddSurveyAnswers();

  const allQuestions = surveyDetails?.questionGroups.flatMap((group) => group.questions) || [];
  const defaultValues = allQuestions.reduce<SurveyRespondFormValues>((acc, question) => {
    if (question.type === "rating") {
      acc[question.id] = {
        rating: 3,
      };
    } else if (question.type === "ranking") {
      acc[question.id] = {
        ranking: question.answers?.map((answer) => ({
          answerId: answer.id,
          sort: question.answers?.indexOf(answer) || 0,
        })),
      };
    } else {
      acc[question.id] = {};
    }

    return acc;
  }, {});
  const form = useForm<SurveyRespondFormValues>({
    defaultValues,
    values: defaultValues,
  });

  if (isPendingSurveyDetails) {
    return <FullSizeLoader withPadding />;
  }

  const error = errorSurveyDetails;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (surveyDetails.endsAt && new Date(surveyDetails.endsAt).getTime() < Date.now()) {
    return <ErrorPage error={404} />;
  }

  const handleSubmit = async (formValues: SurveyRespondFormValues) => {
    await addSurveyAnswersMutation.mutateAsync({
      surveyId: surveyId!,
      data: {
        answers: Object.entries(formValues).map(([questionId, answer]) => ({
          questionId,
          ...answer,
        })),
      },
    });

    postHog?.capture("submitted_survey_respond_form", {
      survey_id: surveyId,
      source: "web",
    });
    successModalHandler.setTrue();
  };

  return (
    <>
      <DocumentPaper
        title={t("page.survey-respond.title")}
        subTitle={
          <Breadcrumbs
            pages={[
              {
                name: t("page.message-feed.title"),
                to: routes.messageFeed.list({ slug }),
              },
              {
                name: t("page.survey-respond.title"),
              },
            ]}
          />
        }
      >
        <PageGrid.Grid>
          <PageGrid.Item size="66%">
            <Form formMethods={form} onSubmit={handleSubmit}>
              <div className="flex w-full flex-col gap-4">
                <SurveyInfoSection survey={surveyDetails} showCountdown />
                {surveyDetails.scope === "project" &&
                  surveyDetails.questionGroups[0].questions.map((question) => (
                    <SurveyRespondQuestionCard key={question.id} {...{ question }} />
                  ))}
                {surveyDetails.scope === "automated" &&
                  surveyDetails.questionGroups.map((questionGroup) => (
                    <SurveyRespondQuestionGroupCard key={questionGroup.order} {...{ questionGroup }} />
                  ))}

                <div className="flex w-full justify-end gap-2 rounded-lg bg-white p-4">
                  <Button onClick={() => navigate(-1)} styling="secondary">
                    {t("common.action.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!!addSurveyAnswersMutation.submittedAt}
                    isLoading={addSurveyAnswersMutation.isPending}
                  >
                    {t("page.survey-respond.form.button.submit")}
                  </Button>
                </div>
              </div>
            </Form>
          </PageGrid.Item>
        </PageGrid.Grid>
      </DocumentPaper>

      <SurveyRespondSuccessModal
        message={surveyDetails.completedMessage}
        isOpened={isSuccessModalOpened}
        onOpenChange={successModalHandler.set}
      />
    </>
  );
}
